import {
  Button,
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  List,
  ShowButton,
  Space,
  Table,
  TableProps,
  notification,
} from '@pankod/refine-antd';
import { CanAccess, IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import { SearchNew } from 'components';
import CustomTableWithPagination from 'components/table/custom-table';
import { IWarehouse, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { RiArchiveLine } from 'react-icons/ri';
import { SearchIndex } from 'services/meilisearch';
import { getWarehouses, getWarehousesExport } from 'services/warehouse';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import { formatCurrency } from 'utilities/shared';
import styles from './style.module.css';

export const WarehouseList: React.FC<IResourceComponentsProps> = () => {
  const [warehouses, setWarehouses] = useState<IWarehouse[]>();
  const [totalWarehouses, setTotalWarehouses] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);

  const fetchDataOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWarehouses({
        searchString: searchText,
        start: page,
        limit,
      });
      const warehouseData = data.data;
      setWarehouses(warehouseData);
      setTotalWarehouses(data.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [limit, page, searchText]);

  useEffect(() => {
    fetchDataOnMount();
  }, [fetchDataOnMount]);

  const { push } = useNavigation();

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getWarehouses();
      const warehouseData = data.data;
      setWarehouses(warehouseData);
      setTotalWarehouses(data.total);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const warehouseData = await getWarehousesExport();
      const rows = [['Unique ID', 'Name', 'Email', 'Area', 'Total Stock Value']];
      warehouseData.forEach((element) => {
        const area = element.address.area;
        rows.push([
          element.warehouseCode,
          element.name,
          element.email,
          area,
          element.totalStockValue.toString(),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <div className={styles['page-header__actions']}>
      <CreateButton type="primary" />
      <Button
        size="middle"
        icon={<Icons.ImportOutlined />}
        onClick={() => push('/warehouses/import')}
      >
        Import
      </Button>
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
      <CanAccess resource="activity-history" action="list">
        <Button
          icon={<Icons.HistoryOutlined />}
          onClick={() => push(getActivityHistoryUrl('warehouse'))}
        >
          Activity
        </Button>
      </CanAccess>
    </div>
  );

  const onTableChange: TableProps<IWarehouse>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra,
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <SearchNew
        placeholder="Search by ID, Code, Name, Email, Area"
        size="large"
        className={styles.search}
        index={SearchIndex.Warehouse}
        groups={['id', 'warehouseCode', 'name', 'email', 'area']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <CustomTableWithPagination
        total={totalWarehouses}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={warehouses}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: true }}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalWarehouses,
          //   current: page,
          // }}
          pagination={false}
        >
          <Table.Column<IWarehouse> dataIndex="warehouseCode" title="Unique ID" />
          <Table.Column<IWarehouse> dataIndex="name" title="Name" />
          <Table.Column<IWarehouse> dataIndex="email" title="Email" />
          <Table.Column<IWarehouse> dataIndex={['address', 'area']} title="Area" />
          <Table.Column<IWarehouse>
            dataIndex="totalStockValue"
            title="Total Stock Value"
            render={(_, record) => formatCurrency(record.totalStockValue)}
          />
          <Table.Column<IWarehouse>
            title="Actions"
            dataIndex="actions"
            width={120}
            render={(_text, record): React.ReactNode => {
              const endpoint = `${record.id}/products`;
              return (
                <Space>
                  <Button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    size="small"
                    icon={<RiArchiveLine />}
                    onClick={() => push(endpoint)}
                  />
                  <ShowButton
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    size="small"
                    recordItemId={record.id}
                    hideText
                  />
                  <EditButton
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    size="small"
                    recordItemId={record.id}
                    hideText
                  />
                  <DeleteButton
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    size="small"
                    hideText
                    recordItemId={record.id}
                    onSuccess={onDeleteSuccessHandler}
                  />
                </Space>
              );
            }}
          />
        </Table>
      </CustomTableWithPagination>
    </List>
  );
};
